// src/pages/Public/LandingPage/v2/i18n/LandingLangContext.js
//
// Estado de idioma de la landing (ES/EN/FR). Diccionario propio, sin i18next:
// es texto de marketing y no se va a fusionar con los ficheros por area del
// portal. Persiste la eleccion en localStorage y detecta el idioma del
// navegador la primera vez.
//
// DESDE 2026-08-09 la eleccion VIAJA AL PORTAL. Quien entra en la web en
// frances y hace login espera encontrarselo en frances, no volver al castellano
// de golpe. Las reglas de ese puente —y por que solo propaga una eleccion
// deliberada, nunca la deteccion del navegador— estan en
// `src/i18n/puenteLandingPortal.js`.

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { LANDING_STRINGS, SUPPORTED_LANGS } from "./landingStrings";
import { propagarIdiomaAlPortal } from "../../../../../i18n/puenteLandingPortal";

// Se exporta para que el puente con el portal no tenga que duplicarla: dos
// copias de una clave de almacenamiento se separan a la primera de cambio.
export const LANDING_STORAGE_KEY = "revi_landing_lang";
const STORAGE_KEY = LANDING_STORAGE_KEY;

function detectInitialLang() {
  // 1) preferencia guardada
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
  } catch (_e) {
    /* localStorage no disponible */
  }
  // 2) idioma del navegador
  try {
    const nav = (navigator.language || "es").slice(0, 2).toLowerCase();
    if (SUPPORTED_LANGS.includes(nav)) return nav;
  } catch (_e) {
    /* navigator no disponible */
  }
  // 3) por defecto espanol
  return "es";
}

const LandingLangContext = createContext(null);

export function LandingLangProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGS.includes(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_e) {
      /* ignore */
    }
    // Y que el portal lo siga, si lo tiene encendido. Va aqui y no en
    // `detectInitialLang` a proposito: solo propaga lo que alguien ha ELEGIDO.
    propagarIdiomaAlPortal(next);
  }, []);

  // t(key): devuelve la cadena del idioma activo, con fallback a ES y luego a la clave.
  const t = useCallback(
    (key) => {
      const dict = LANDING_STRINGS[lang] || LANDING_STRINGS.es;
      return dict[key] ?? LANDING_STRINGS.es[key] ?? key;
    },
    [lang],
  );

  const value = useMemo(
    () => ({ lang, setLang, t, langs: SUPPORTED_LANGS }),
    [lang, setLang, t],
  );

  return (
    <LandingLangContext.Provider value={value}>
      {children}
    </LandingLangContext.Provider>
  );
}

export function useLandingLang() {
  const ctx = useContext(LandingLangContext);
  if (!ctx) {
    throw new Error(
      "useLandingLang debe usarse dentro de <LandingLangProvider>",
    );
  }
  return ctx;
}
